import {
  SET_APPLICATIONS_LIST,
  ADD_APPLICATION,
  UPDATE_APPLICATION,
  SET_CURRENT_APPLICATION,
} from '../actionTypes';

// ---------------------------
// (1) Type definitions
// ---------------------------

// The simpler "core" details that might have existed previously
export type ApplicationDetails = {
  id: string;
  company: {
    id: string;
    name: string;
  };
  farm: {
    id: string;
    name: string;
    farmArrangement: string;
  };
  user: {
    externalId: string;
  };
  applyingUser: {
    id: string;
    idNumber: string;
    externalId: string;
  };
};

export type ApplicationDoc = {
  id: string;
  documentType: string;
  filePath: string;
  createdAt: string;
  updatedAt: string;
};

export type CustomerSupplier = {
  id: string;
  crop: {id: string; name: string};
  customer: {id: string; name: string};
};

export type FarmRisk = {
  id: string;
  mitigation: string;
  problem: string;
  risk: {id: string; name: string};
};

export type ProjectedRevenue = {
  id: string;
  monthlyCosts: string;
  year: number;
};

export type OperatingCost = {
  id: string;
  monthlyCosts: string;
  year: number;
  expense: {id: string; name: string};
};

export type ProductionPlan = {
  id: string;
  plantingDate: string;
  numberOfHectares: number;
  estimatedYield: number;
  plantPopulation: number;
  irrigation: string;
  crop: {id: string; name: string};
};

export type FundingAskCost = {
  id: string;
  label: string;
  cost: number;
};

export type FundingAsk = {
  id: string;
  fundingRequestCosts: FundingAskCost[];
};

export type FinancialPerformance = {
  id: string;
  year: number;
  revenue: number;
  expenses: number;
  profit: number;
};

// (2) The "full" expanded shape:
export type FullApplication = {
  id: string;
  applicationDocs: ApplicationDoc[];
  customerSuppliers: CustomerSupplier[];
  farmRisks: FarmRisk[];
  projectedRevenues: ProjectedRevenue[];
  operatingCosts: OperatingCost[];
  productionPlans: ProductionPlan[];
  fundingAsks: FundingAsk;
  financialPerformances: FinancialPerformance[];
  applicationDetails: ApplicationDetails;
};

// (3) Our Redux state uses FullApplication everywhere
export type ApplicationState = {
  lastUpdate: number;
  currentApplication: FullApplication | null;
  applicationsList: FullApplication[];
};

// ---------------------------
// (4) Initial Data
// ---------------------------

// A "full" application object
const initialApplication: FullApplication = {
  id: 'initial',
  applicationDocs: [
    {
      id: '1',
      documentType: 'PROOF_OF_ADDRESS',
      filePath: 'testing',
      createdAt: '2025-02-24T12:14:16.955Z',
      updatedAt: '2025-02-24T12:14:16.955Z',
    },
  ],
  customerSuppliers: [
    {
      id: '1',
      crop: {id: '1', name: 'Carrots'},
      customer: {id: '1', name: 'Pick n Pay'},
    },
    {
      id: '2',
      crop: {id: '2', name: 'Chillie'},
      customer: {id: '1', name: 'Pick n Pay'},
    },
    // ... (rest of your sample data)
    {
      id: '18',
      crop: {id: '26', name: 'Basil'},
      customer: {id: '4', name: 'Woolworths'},
    },
  ],
  farmRisks: [
    {
      id: '1',
      mitigation: 'There',
      problem: 'There',
      risk: {id: '1', name: 'Weather'},
    },
    // ... (rest of your sample data)
    {
      id: '9',
      mitigation: 'World',
      problem: 'World',
      risk: {id: '5', name: 'Yield Risk'},
    },
  ],
  projectedRevenues: [
    {
      id: '1',
      monthlyCosts: '{"endMonth":"December","startMonth":"September"}',
      year: 2025,
    },
  ],
  operatingCosts: [
    {
      id: '1',
      monthlyCosts:
        '{"total":"3000","months":["January","March","April","May","June","July","August","October","December"],' +
        '"quarters":[],"frequency":"monthly"}',
      year: 2025,
      expense: {id: '6', name: 'Garage Card'},
    },
    {
      id: '2',
      monthlyCosts:
        '{"month":"March","total":"20","months":[],"quarters":[],"frequency":"once-off"}',
      year: 2025,
      expense: {id: '12', name: 'Revolving Credit'},
    },
  ],
  productionPlans: [
    {
      id: '10',
      plantingDate: '2025-03-01T00:00:00.000Z',
      numberOfHectares: 10,
      estimatedYield: 20,
      plantPopulation: 40,
      irrigation: 'IRRIGATED',
      crop: {id: '1', name: 'Carrots'},
    },
    // ... (rest of your sample data)
    {
      id: '12',
      plantingDate: '2025-03-01T00:00:00.000Z',
      numberOfHectares: 50,
      estimatedYield: 60,
      plantPopulation: 45,
      irrigation: 'IRRIGATED',
      crop: {id: '1', name: 'Carrots'},
    },
  ],
  fundingAsks: {
    id: '1',
    fundingRequestCosts: [
      {id: '6', label: 'Garage Card', cost: 3000},
      {id: '12', label: 'Revolving Credit', cost: 20},
    ],
  },
  financialPerformances: [
    {id: '4', year: 2023, revenue: 300, expenses: 100, profit: 200},
    {id: '5', year: 2024, revenue: 500, expenses: 200, profit: 300},
    {id: '6', year: 2022, revenue: 40, expenses: 0, profit: 40},
  ],
  applicationDetails: {
    id: '14',
    company: {id: '16', name: ''},
    farm: {id: '16', name: '', farmArrangement: 'NOT_YET_ADDED'},
    user: {externalId: '3366'},
    applyingUser: {id: '16', idNumber: '9383039383732', externalId: '3366'},
  },
};

// Two "full" entries for our applications list
const initialApplicationsList: FullApplication[] = [
  {
    id: '14',
    applicationDocs: [],
    customerSuppliers: [],
    farmRisks: [],
    projectedRevenues: [],
    operatingCosts: [],
    productionPlans: [],
    fundingAsks: {
      id: 'FA1',
      fundingRequestCosts: [],
    },
    financialPerformances: [],
    applicationDetails: {
      id: '14',
      company: {id: '16', name: 'ABC Trading'},
      farm: {id: '16', name: 'Sunny Farm', farmArrangement: 'LEASED'},
      user: {externalId: '3366'},
      applyingUser: {
        id: '16',
        idNumber: '9383039383732',
        externalId: '3366',
      },
    },
  },
  {
    id: '15',
    applicationDocs: [],
    customerSuppliers: [],
    farmRisks: [],
    projectedRevenues: [],
    operatingCosts: [],
    productionPlans: [],
    fundingAsks: {
      id: 'FA2',
      fundingRequestCosts: [],
    },
    financialPerformances: [],
    applicationDetails: {
      id: '15',
      company: {id: '17', name: 'XYZ Corp'},
      farm: {id: '17', name: 'Green Farm', farmArrangement: 'OWNED'},
      user: {externalId: '3367'},
      applyingUser: {
        id: '17',
        idNumber: '9383039383733',
        externalId: '3367',
      },
    },
  },
];

// ---------------------------
// (5) The initial Redux state
// ---------------------------
const initialState: ApplicationState = {
  lastUpdate: Date.now(),
  currentApplication: initialApplication, // full object
  applicationsList: initialApplicationsList, // array of full objects
};

// ---------------------------
// (6) Reducer function
// ---------------------------
export default function (
  state = initialState,
  action: {type: string; payload: any},
): ApplicationState {
  switch (action.type) {
    case SET_APPLICATIONS_LIST: {
      // Now we expect to receive an array of FullApplication
      const applications: FullApplication[] = action.payload || [];
      return {
        ...state,
        lastUpdate: Date.now(),
        applicationsList: applications,
      };
    }

    case ADD_APPLICATION: {
      // We expect the payload to be a FullApplication
      const application: FullApplication = action.payload;
      return {
        ...state,
        lastUpdate: Date.now(),
        applicationsList: [...state.applicationsList, application],
      };
    }

    case UPDATE_APPLICATION: {
      // We expect the payload to be a FullApplication
      const updatedApplication: FullApplication = action.payload;
      return {
        ...state,
        lastUpdate: Date.now(),
        applicationsList: state.applicationsList.map((app) =>
          app.id === updatedApplication.id ? {...updatedApplication} : app,
        ),
        currentApplication:
          state.currentApplication &&
          state.currentApplication.id === updatedApplication.id
            ? {...state.currentApplication, ...updatedApplication}
            : state.currentApplication,
      };
    }

    case SET_CURRENT_APPLICATION: {
      const applicationId: string = action.payload;
      return {
        ...state,
        currentApplication:
          state.applicationsList.find((app) => app.id === applicationId) ||
          null,
      };
    }

    default:
      return state;
  }
}
